import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Lock, Mail, ShieldCheck, ArrowRight, UserCheck } from "lucide-react";

export const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please enter both email and password.");
      return;
    }
    setSubmitting(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name}!`);
      if (user.role === "admin") {
        navigate("/admin/desk");
      } else if (user.role === "landlord") {
        navigate("/landlord/dashboard");
      } else {
        navigate("/tenant/dashboard");
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Invalid email or password";
      toast.error(typeof msg === "string" ? msg : "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  // Quick helper to fill test credentials
  const fillCredentials = (role) => {
    if (role === "admin") {
      setEmail("admin@findlet.co.uk");
      setPassword("AdminPass2026!");
    } else if (role === "landlord") {
      setEmail("richard.sterling@example.com");
      setPassword("LandlordPass2026!");
    } else {
      setEmail("eleanor.vane@example.com");
      setPassword("TenantPass2026!");
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FBFF] flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md text-center space-y-2">
        <Link to="/" className="inline-flex items-center space-x-2 text-2xl font-bold font-heading uppercase text-[#091F5C]">
          <span>FIND</span>
          <span className="text-[#334DAF]">|</span>
          <span>LET</span>
        </Link>
        <h2 className="text-2xl font-extrabold text-[#091F5C] font-heading">
          Sign In to Your Portal
        </h2>
        <p className="text-xs text-[#4A5B78]">
          Enter your registered email and password to access your London brief or listings.
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md px-4">
        <div className="bg-white py-8 px-6 sm:px-10 rounded-2xl border border-[#E5EBF5] shadow-xl space-y-6">
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-1.5">
                Email Address
              </label>
              <div className="relative">
                <Mail className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
                <input
                  type="email"
                  required
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input"
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm focus:outline-none focus:ring-2 focus:ring-[#334DAF]"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-[#4A5B78] uppercase tracking-wider mb-1.5">
                Password
              </label>
              <div className="relative">
                <Lock className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
                <input
                  type="password"
                  required
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-[#E5EBF5] text-sm focus:outline-none focus:ring-2 focus:ring-[#334DAF]"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="login-submit-button"
              className="w-full py-3 rounded-xl bg-[#091F5C] hover:bg-[#061745] text-white font-bold text-sm shadow-md transition-all flex items-center justify-center space-x-2 disabled:opacity-50"
            >
              <span>{submitting ? "Signing In..." : "Sign In"}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>

          {/* Quick Account Fill Helpers */}
          <div className="pt-4 border-t border-slate-100 space-y-2">
            <span className="block text-[11px] font-semibold text-slate-400 uppercase tracking-wider text-center">
              Quick Test Account Presets:
            </span>
            <div className="grid grid-cols-3 gap-1.5 text-xs">
              <button
                type="button"
                onClick={() => fillCredentials("tenant")}
                data-testid="quick-login-tenant"
                className="p-1.5 rounded-lg border border-[#E5EBF5] hover:border-[#D0E4FE] hover:bg-[#E8F2FE] text-[#4A5B78] font-medium"
              >
                Tenant
              </button>
              <button
                type="button"
                onClick={() => fillCredentials("landlord")}
                data-testid="quick-login-landlord"
                className="p-1.5 rounded-lg border border-[#E5EBF5] hover:border-[#D0E4FE] hover:bg-[#E8F2FE] text-[#4A5B78] font-medium"
              >
                Landlord
              </button>
              <button
                type="button"
                onClick={() => fillCredentials("admin")}
                data-testid="quick-login-admin"
                className="p-1.5 rounded-lg border border-[#E5EBF5] hover:border-[#D0E4FE] hover:bg-[#E8F2FE] text-[#4A5B78] font-medium"
              >
                Concierge
              </button>
            </div>
          </div>

          <div className="text-center text-xs text-[#4A5B78] pt-2">
            Don't have an account yet?{" "}
            <Link to="/register" className="text-[#334DAF] font-bold hover:underline">
              Register now
            </Link>
          </div>

        </div>
      </div>
    </div>
  );
};
